// Shared plan-premium data loader.
//
// calculateEnrollmentPremium can only resolve a BUY-UP plan's employer
// contribution when it receives allPlanContributions AND allPlanRates that
// include the referenced BASE plan's rows (a payer:"base" contribution row
// points at a cheaper base plan whose rates × percentages define the employer
// dollar share). Every surface that computes an employee/employer premium
// split must therefore fetch the base plans too — this module centralises that
// so no call site can forget it.
//
// Two forms:
//   usePlanPremiumData(planIds, clientId)  — React hook (components)
//   fetchPlanPremiumData(planIds, clientId) — imperative (async handlers, CSV
//                                             export, effects)
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import type { RatingAreaGap } from "@shared/utils/ratingAreaCoverage";

export interface PlanPremiumData {
  /** Every contribution row (all plans). Complete because it's fetched
   * unfiltered — so base plans' contribution rows are always present. */
  allPlanContributions: any[];
  /** Rate rows for the requested plans + their referenced base plans. */
  allPlanRates: any[];
  /** Rates keyed by planId (requested + base). */
  ratesByPlanId: Record<number, any[]>;
  /** Contributions keyed by planId (requested + base). */
  contribsByPlanId: Record<number, any[]>;
  /** DEV-382: plans whose rate table has rates, but none for the group's
   * rating area (their ratesByPlanId entry is empty). Never price these as $0. */
  ratingAreaGapsByPlanId?: Record<number, RatingAreaGap>;
  /** DEV-389: plans whose rates are split by rating area while no area
   * resolved for the group (all rows returned, but unpriceable). */
  ratingAreaUnresolvedByPlanId?: Record<number, boolean>;
  isLoading: boolean;
  /** Loader failure message (the query threw), else null. Consumers must not
   * price against the empty fallback as if it were a $0 premium (BUG-006). */
  error?: string | null;
}

const EMPTY: PlanPremiumData = {
  allPlanContributions: [],
  allPlanRates: [],
  ratesByPlanId: {},
  contribsByPlanId: {},
  ratingAreaGapsByPlanId: {},
  ratingAreaUnresolvedByPlanId: {},
  isLoading: false,
  error: null,
};

/** Base plan ids referenced (payer:"base" → basePlanId) by the given plans'
 * contribution rows. */
export function referencedBasePlanIds(contributions: any[], planIds: number[]): number[] {
  const wanted = new Set(planIds.map(Number));
  const ids = new Set<number>();
  for (const c of contributions || []) {
    const bp = Number((c as any)?.basePlanId);
    if (
      String((c as any)?.payer) === "base" &&
      Number.isFinite(bp) &&
      wanted.has(Number((c as any)?.planId))
    ) {
      ids.add(bp);
    }
  }
  return Array.from(ids);
}

/** Imperative loader: contributions (all) + rates for plans and their base
 * plans. Use inside async handlers / effects where a hook can't run. */
export async function fetchPlanPremiumData(
  planIds: number[],
  clientId?: number | string | null,
  // DEV-262: optional rate-version date (YYYY-MM-DD). Quoting passes the
  // prospective plan-year start so the server serves the rate version in
  // force for that date (spec scenario 17) instead of today's.
  asOf?: string | null,
  // Optional rating region (e.g. CA 1..19). Region-specific rate rows win over
  // region-agnostic ones so multi-region library plans price the right area.
  ratingAreaId?: number | null,
): Promise<PlanPremiumData> {
  const ids = Array.from(new Set((planIds || []).map(Number).filter(Number.isFinite)));
  // Contributions are neither plan- nor client-scoped (the endpoint returns the
  // full set), so share ONE cache entry app-wide instead of re-downloading the
  // whole table per plan-id set. Fail loud: a partial load (missing
  // contributions or, below, missing base rates) would silently over-credit the
  // employer on buy-up plans, so throw to surface an error/retry rather than
  // render wrong money.
  const allPlanContributions: any[] = await queryClient.fetchQuery({
    queryKey: ["/api/plan-contributions"],
    queryFn: async () => {
      const res = await fetch(`/api/plan-contributions`);
      if (!res.ok) throw new Error(`Failed to load plan contributions (${res.status})`);
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const ratePlanIds = Array.from(
    new Set([...ids, ...referencedBasePlanIds(allPlanContributions, ids)]),
  );

  const ratesByPlanId: Record<number, any[]> = {};
  const ratingAreaGapsByPlanId: Record<number, RatingAreaGap> = {};
  const ratingAreaUnresolvedByPlanId: Record<number, boolean> = {};
  if (ratePlanIds.length > 0) {
    const idsParam = ratePlanIds.join(",");
    let url =
      clientId != null && clientId !== ""
        ? `/api/plan-rates/bulk?planIds=${idsParam}&clientId=${clientId}`
        : `/api/plan-rates/bulk?planIds=${idsParam}`;
    if (asOf) url += `&asOf=${asOf}`;
    if (ratingAreaId != null) url += `&ratingAreaId=${ratingAreaId}`;
    const rRes = await fetch(url);
    if (!rRes.ok) throw new Error(`Failed to load plan rates (${rRes.status})`);
    const json = await rRes.json();
    for (const id of ratePlanIds) {
      ratesByPlanId[id] = (json as any)?.[id]?.rates || [];
      const gap = (json as any)?.[id]?.ratingAreaGap;
      if (gap && Number.isFinite(Number(gap.ratingAreaId))) ratingAreaGapsByPlanId[id] = gap;
      if ((json as any)?.[id]?.ratingAreaUnresolved) ratingAreaUnresolvedByPlanId[id] = true;
    }
  }

  const allPlanRates = Object.values(ratesByPlanId).flat();
  const contribsByPlanId: Record<number, any[]> = {};
  for (const id of ratePlanIds) {
    contribsByPlanId[id] = allPlanContributions.filter((c: any) => Number(c?.planId) === id);
  }

  return {
    allPlanContributions,
    allPlanRates,
    ratesByPlanId,
    contribsByPlanId,
    ratingAreaGapsByPlanId,
    ratingAreaUnresolvedByPlanId,
    isLoading: false,
    error: null,
  };
}

/** Hook form. Returns stable data + loading flag; the flattened arrays feed
 * straight into calculateEnrollmentPremium / computeEnrollmentPremiumResult. */
export function usePlanPremiumData(
  planIds: number[],
  clientId?: number | string | null,
  asOf?: string | null,
  ratingAreaId?: number | null,
): PlanPremiumData {
  const sortedIds = useMemo(
    () => Array.from(new Set((planIds || []).map(Number).filter(Number.isFinite))).sort((a, b) => a - b),
    [planIds],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["plan-premium-data", sortedIds, clientId ?? null, asOf ?? null, ratingAreaId ?? null],
    enabled: sortedIds.length > 0,
    staleTime: 60_000,
    queryFn: () => fetchPlanPremiumData(sortedIds, clientId, asOf, ratingAreaId),
  });

  const errorMessage = error ? String((error as any)?.message ?? error) : null;
  return data
    ? { ...data, isLoading, error: null }
    : { ...EMPTY, isLoading: sortedIds.length > 0 && isLoading, error: errorMessage };
}
